import { useEffect, useState, useCallback } from "react";
import { EmailSuggestInput } from "../../components/EmailSuggestInput";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AdminDashboard } from "./AdminDashboard";
import { LogOut, Download, RefreshCw, Inbox, Briefcase, ExternalLink, StickyNote, Trash2, ChevronDown, FileText, Wrench, LayoutDashboard, LayoutGrid, Users, UserPlus, CreditCard, Package, PenLine, Mail, Building2, FileSignature, IdCard, CalendarDays, CalendarOff, Send, Lock, BadgeIndianRupee, GraduationCap } from "lucide-react";
import { LOGO } from "../../data/content";
import { AdminAnalytics } from "./AdminAnalytics";
import { AdminTeam } from "./AdminTeam";
import { AdminLetters } from "./AdminLetters";
import { AdminIdCards } from "./AdminIdCards";
import { AdminSalary } from "./AdminSalary";
import { AdminPayments } from "./AdminPayments";
import { AdminProducts } from "./AdminProducts";
import { AdminSignature } from "./AdminSignature";
import { AdminCareerApps } from "./AdminCareerApps";
import { AdminPeople } from "./AdminPeople";
import AdminEmailSetup from "./AdminEmailSetup";
import AdminHolidays from "./AdminHolidays";
import AdminLeave from "./AdminLeave";
import AdminTraining from "./AdminTraining";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });
// Suspended / terminated accounts are signed out on their very next request; the sign-in screen then shows the HR text.
api.interceptors.response.use((r) => r, (e) => {
  const d = e?.response?.data?.detail;
  if (e?.response?.status === 401 && typeof d === "string" && (d.startsWith("Your account has been suspended") || d === "This account does not exist.")) {
    sessionStorage.setItem("nx_login_notice", d);
    window.dispatchEvent(new Event("nx-force-logout"));
  }
  return Promise.reject(e);
});

const fmtError = (d) => {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || "Something went wrong.";
};

const fmtDate = (d) => new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const Login = ({ onSuccess }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(() => { const m = sessionStorage.getItem("nx_login_notice"); sessionStorage.removeItem("nx_login_notice"); return m || ""; });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      onSuccess(data);
    } catch (err) {
      const msg = fmtError(err?.response?.data?.detail) || "Login failed";
      setError(msg);
      if (![401, 403].includes(err?.response?.status)) toast.error(msg);
    } finally {
      setBusy(false);
    }
  };
  const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";
  return (
    <div className="min-h-screen flex items-center justify-center px-5" data-testid="admin-login">
      <div className="w-full max-w-sm border border-white/10 bg-white/[0.02] p-8">
        <img src={LOGO.fullDark} alt="NexSecure Technologies" className="h-28 w-auto mx-auto mb-8" />
        <p className="text-[10px] tracking-[0.28em] uppercase text-slate-500 text-center mb-6">Team Access</p>
        <form onSubmit={submit} className="flex flex-col gap-4" autoComplete="off">
          <input type="email" required placeholder="Email" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} data-testid="admin-email-input" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck="false" data-lpignore="true" data-1p-ignore="true" data-bwignore="true" />
          <input type="password" required placeholder="Password" className={input} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-password-input" autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" data-bwignore="true" />
          {error && <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 px-3 py-2.5" role="alert" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={busy} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold text-sm tracking-wide px-6 py-3.5 transition-colors disabled:opacity-60" data-testid="admin-login-button">
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <Link to="/" className="block text-center text-xs text-slate-500 hover:text-nx-goldlight mt-6 transition-colors">← Back to website</Link>
      </div>
    </div>
  );
};

const downloadCv = async (path, filename) => {
  try {
    const res = await api.get(`/applications/cv/${path}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "cv.pdf";
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Could not download CV");
  }
};

const downloadFile = async (path, filename) => {
  try {
    const res = await api.get(`/files/${path}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "document";
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Could not download file");
  }
};

const INQUIRY_LABELS = {
  "request-quote": "Request a Quote",
  "amc-inquiry": "AMC Inquiry",
  "site-survey": "Site Survey",
  "dealership": "Dealership",
  "technical-support": "Tech Support",
};

const Row = ({ children, highlight = false, overdue = false }) => (
  <div className={`border p-5 sm:p-6 flex flex-col gap-3 ${overdue ? "border-amber-500/60 bg-amber-500/[0.07]" : highlight ? "border-red-500/40 bg-red-500/[0.05]" : "border-white/10 bg-white/[0.02]"}`}>{children}</div>
);

const Meta = ({ children }) => <span className="text-xs text-slate-500">{children}</span>;

const FilterChips = ({ values, active, onChange, items, prefix }) => (
  <div className="flex flex-wrap gap-2 mb-6" data-testid={`${prefix}-filters`}>
    {values.map((f) => (
      <button
        key={f}
        onClick={() => onChange(f)}
        data-testid={`${prefix}-filter-${f}`}
        className={`text-[11px] tracking-[0.14em] uppercase px-4 py-2 border transition-colors ${
          active === f ? "border-nx-gold/60 text-nx-goldlight bg-nx-gold/[0.06]" : "border-white/10 text-slate-500 hover:text-white"
        }`}
      >
        {f === "all" ? `All (${items.length})` : `${f} (${items.filter((x) => (x.status || "new") === f).length})`}
      </button>
    ))}
  </div>
);

const QuoteCreateForm = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", solution: "", budget: "", timeline: "", requirement: "" });
  const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-600 px-4 py-2.5 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/quotes", f);
      toast.success(`Quotation created for ${f.name} — only you (and admin) can see it`);
      setF({ name: "", company: "", email: "", phone: "", solution: "", budget: "", timeline: "", requirement: "" });
      setOpen(false);
      onCreated();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create the quotation");
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-3 transition-colors mb-6" data-testid="quote-create-open">
        <UserPlus size={14} /> New Quote
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="border border-nx-gold/40 bg-white/[0.02] p-5 sm:p-6 mb-6 flex flex-col gap-3" data-testid="quote-create-form">
      <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500">New quotation — visible only to you</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <input required placeholder="Customer name" className={input} value={f.name} onChange={set("name")} data-testid="quote-create-name" />
        <input placeholder="Company" className={input} value={f.company} onChange={set("company")} data-testid="quote-create-company" />
        <EmailSuggestInput required type="email" placeholder="Customer email" className={input} value={f.email} onChange={set("email")} data-testid="quote-create-email" />
        <input required placeholder="Phone" className={input} value={f.phone} onChange={set("phone")} data-testid="quote-create-phone" />
        <input placeholder="Solution (e.g. CCTV Solutions)" className={input} value={f.solution} onChange={set("solution")} data-testid="quote-create-solution" />
        <input placeholder="Quoted amount / budget" className={input} value={f.budget} onChange={set("budget")} data-testid="quote-create-budget" />
      </div>
      <textarea required rows={2} placeholder="Requirement / scope" className={input} value={f.requirement} onChange={set("requirement")} data-testid="quote-create-requirement" />
      <div className="flex gap-3">
        <button type="submit" disabled={busy} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-6 py-3 transition-colors disabled:opacity-60" data-testid="quote-create-submit">
          {busy ? "Saving…" : "Create Quote"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="text-xs tracking-[0.14em] uppercase text-slate-500 hover:text-white px-4 py-3 transition-colors" data-testid="quote-create-cancel">Cancel</button>
      </div>
    </form>
  );
};

const AssignControl = ({ item, staff, onAssigned }) => {
  const [busy, setBusy] = useState(false);
  const save = async (assigned_to, job_nature) => {
    setBusy(true);
    try {
      await api.patch(`/service-requests/${item.id}/assign`, { assigned_to: assigned_to || null, job_nature });
      onAssigned();
      toast.success(assigned_to ? `Assigned to ${assigned_to}` : "Assignment removed");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not assign");
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="flex flex-wrap items-center gap-2" data-testid={`sr-assign-${item.id}`}>
      <UserPlus size={13} className="text-nx-goldlight" />
      <select
        value={item.assigned_to || ""}
        disabled={busy}
        onChange={(e) => save(e.target.value, item.job_nature || "field")}
        className="bg-white/[0.04] border border-white/15 text-slate-200 px-3 py-1.5 text-xs focus:outline-none focus:border-nx-goldlight/70"
        data-testid={`sr-assign-select-${item.id}`}
      >
        <option value="" className="text-neutral-900">Unassigned</option>
        {staff.map((m) => (
          <option key={m.email} value={m.email} className="text-neutral-900">{m.name} ({m.email})</option>
        ))}
      </select>
      <select
        value={item.job_nature || "field"}
        disabled={busy}
        onChange={(e) => save(item.assigned_to || null, e.target.value)}
        className="bg-white/[0.04] border border-white/15 text-slate-200 px-3 py-1.5 text-xs focus:outline-none focus:border-nx-goldlight/70"
        data-testid={`sr-nature-select-${item.id}`}
      >
        <option value="field" className="text-neutral-900">Field job</option>
        <option value="remote" className="text-neutral-900">Remote / desk</option>
      </select>
    </span>
  );
};

const StatusFlow = ({ kind, item, onChange }) => {
  const s = item.status || "new";
  const btn = "text-[11px] tracking-[0.1em] uppercase border px-3 py-1.5 transition-colors";
  return (
    <span className="flex flex-wrap gap-2 ml-auto">
      {s === "new" && (
        <button onClick={() => onChange(kind, item.id, "contacted")} className={`${btn} text-white border-white/20 hover:border-nx-gold/60`} data-testid={`${kind}-mark-contacted-${item.id}`}>Mark Contacted</button>
      )}
      {s !== "closed" && (
        <button onClick={() => onChange(kind, item.id, "closed")} className={`${btn} text-slate-400 border-white/20 hover:border-slate-400`} data-testid={`${kind}-mark-closed-${item.id}`}>Close</button>
      )}
      {s !== "new" && (
        <button onClick={() => onChange(kind, item.id, "new")} className="text-[11px] tracking-[0.1em] uppercase text-slate-500 hover:text-white px-3 py-1.5 transition-colors" data-testid={`${kind}-reopen-${item.id}`}>Reopen</button>
      )}
    </span>
  );
};

const StaffReply = ({ item, onUpdate }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const replies = item.staff_replies || [];
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/enquiries/${item.id}/staff-reply`, { text: text.trim() });
      onUpdate(data);
      setText("");
      toast.success("Reply delivered on Telegram");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the reply");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border border-red-500/30 bg-red-500/[0.04] p-3 flex flex-col gap-2" data-testid={`staff-reply-${item.id}`}>
      <p className="text-[10px] tracking-[0.18em] uppercase text-red-300 flex items-center gap-2"><Send size={11} /> Telegram conversation with staff ({replies.length})</p>
      {replies.map((r) => (
        <div key={r.id} className={`text-xs border-l-2 pl-3 ${r.from_staff ? "border-nx-gold/60 text-slate-200" : "border-red-400/50 text-slate-300"}`} data-testid={`staff-reply-item-${r.id}`}>
          <span className="text-slate-500">{r.from_staff ? <span className="text-nx-goldlight">Staff · </span> : <span className="text-red-300">CEO · </span>}{r.by} · {fmtDate(r.at)}</span>
          <p className="mt-0.5 whitespace-pre-wrap">{r.text}</p>
        </div>
      ))}
      <div className="flex gap-2">
        <textarea rows={2} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your reply — it is sent to the staff member's Telegram chat by the support bot" className="flex-1 bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-3 py-2 text-sm focus:outline-none focus:border-red-400/60 resize-none" data-testid={`staff-reply-input-${item.id}`} />
        <button onClick={send} disabled={busy || !text.trim()} className="self-end inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-500 text-white text-[11px] font-semibold tracking-[0.1em] uppercase px-4 py-2.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`staff-reply-send-${item.id}`}>
          <Send size={12} /> {busy ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
};

const Notes = ({ kind, item, onUpdate, readOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const notes = item.notes || [];

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/${kind}/${item.id}/notes`, { text: text.trim() });
      onUpdate(data);
      setText("");
    } catch {
      toast.error("Could not save note");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (noteId) => {
    try {
      const { data } = await api.delete(`/${kind}/${item.id}/notes/${noteId}`);
      onUpdate(data);
    } catch {
      toast.error("Could not delete note");
    }
  };

  return (
    <div className="border-t border-white/5 pt-3" data-testid={`notes-${kind}-${item.id}`}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase text-slate-500 hover:text-nx-goldlight transition-colors" data-testid={`notes-toggle-${item.id}`}>
        <StickyNote size={13} className="text-nx-goldlight" /> Internal notes ({notes.length})
        <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-4 flex flex-col gap-3">
          {notes.length === 0 && <p className="text-xs text-slate-600">No notes yet — only signed-in team members can see these.</p>}
          {notes.map((n) => (            <div key={n.id} className="flex flex-wrap items-start justify-between gap-3 sm:gap-4 border border-white/10 bg-white/[0.02] px-4 py-3" data-testid={`note-${n.id}`}>
              <div>
                <p className="text-sm text-slate-300 leading-relaxed">{n.text}</p>
                <Meta>{n.author} · {fmtDate(n.created_at)}</Meta>
              </div>
              {!readOnly && (
              <button onClick={() => remove(n.id)} className="text-slate-600 hover:text-red-400 transition-colors p-1 shrink-0" aria-label="Delete note" data-testid={`note-delete-${n.id}`}>
                <Trash2 size={13} />
              </button>
              )}
            </div>
          ))}
          {!readOnly && (
          <div className="flex gap-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="Add a private note — e.g. called, site visit booked…"
              className="flex-1 bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-600 px-4 py-2.5 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors"
              data-testid={`note-input-${item.id}`}
            />
            <button onClick={add} disabled={busy || !text.trim()} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-wide px-5 py-2.5 transition-colors disabled:opacity-50" data-testid={`note-add-${item.id}`}>
              Add
            </button>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

const Dashboard = ({ user, onLogout, initialTab }) => {
  const [tab, setTab] = useState(initialTab || "home");

  const [enquiries, setEnquiries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [assignableStaff, setAssignableStaff] = useState([]);
  const [lettersPending, setLettersPending] = useState(0);
  const [salaryPending, setSalaryPending] = useState(0);
  const [idCardPrefill, setIdCardPrefill] = useState(null);
  const [letterPrefill, setLetterPrefill] = useState(null);
  const shortlistToLetters = async (a) => {
    if ((a.status || "new") !== "shortlisted") await markAppStatus(a.id, "shortlisted");
    setLetterPrefill({ employee_name: a.name, personal_email: a.email, phone: a.phone || "", designation: a.role || "" });
    setTab("letters");
  };
  const [loading, setLoading] = useState(true);

  // ---- role-based access: sections come from the backend (/auth/me) ----
  const sections = user.sections || {};
  const canManage = (sec) => sections[sec] === "manage";
  const canAssignJobs = ["admin", "manager", "operations", "support"].includes(user.role);
  const TABS = [
    { id: "overview", icon: LayoutDashboard, label: "Overview" },
    { id: "enquiries", icon: Inbox, label: `Enquiries (${enquiries.length})` },
    { id: "inquiries", icon: FileText, label: `Inquiries (${inquiries.length})` },
    { id: "quotes", icon: FileText, label: `Quotes (${quotes.length})` },
    { id: "service-requests", icon: Wrench, label: `Service (${serviceRequests.length})` },
    { id: "applications", icon: Briefcase, label: `Applications (${applications.length})` },
    { id: "career-apps", icon: FileText, label: "Career Apps" },
    { id: "people", icon: Building2, label: "HR & Payroll" },
    { id: "team", icon: Users, label: "Team" },
    { id: "letters", icon: FileSignature, label: `Appointment Letters${lettersPending ? ` (${lettersPending})` : ""}` },
    { id: "idcards", icon: IdCard, label: "ID Cards" },
    { id: "salary", icon: BadgeIndianRupee, label: `Salary Slips${salaryPending ? ` (${salaryPending})` : ""}` },
    { id: "payments", icon: CreditCard, label: `Payments${sections.payments === "view" ? " (view)" : ""}` },
    { id: "products", icon: Package, label: "Store" },
    { id: "signature", icon: PenLine, label: "Signature" },
    { id: "email-setup", icon: Mail, label: "Email Setup" },
    { id: "holidays", icon: CalendarDays, label: "Holidays" },
    { id: "leave", icon: CalendarOff, label: "Leave" },
    { id: "training", icon: GraduationCap, label: "Training Schedule" },
  ];
  const hiddenSections = user.hidden_sections || [];
  const allowedTabs = TABS.filter((t) => sections[t.id] || hiddenSections.includes(t.id));
  const activeTab = tab === "home" ? "home" : allowedTabs.some((t) => t.id === tab && sections[t.id]) ? tab : (allowedTabs.find((t) => sections[t.id])?.id || "overview");
  const [filter, setFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const isCeo = (user.email || "").toLowerCase() === "ceo@nexsecure.in";
  const staffDepartments = [...new Set(enquiries.filter((e) => e.internal).map((e) => e.staff?.department || "Unassigned"))].sort();
  const [appFilter, setAppFilter] = useState("all");
  const [quoteFilter, setQuoteFilter] = useState("all");
  const [srFilter, setSrFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    const fetchers = [
      ["enquiries", "/enquiries", setEnquiries],
      ["applications", "/applications", setApplications],
      ["quotes", "/quotes", setQuotes],
      ["service-requests", "/service-requests", setServiceRequests],
      ["inquiries", "/inquiries", setInquiries],
    ].filter(([sec]) => (user.sections || {})[sec]);
    try {
      await Promise.all(fetchers.map(([, url, setter]) => api.get(url).then((r) => setter(r.data)).catch(() => {})));
      if ((user.sections || {}).letters && user.role === "admin") {
        api.get("/letters/appointment/pending-count").then((r) => setLettersPending(r.data.count || 0)).catch(() => {});
        api.get("/salary/pending-count").then((r) => setSalaryPending((r.data.count || 0) + (r.data.requests || 0))).catch(() => {});
      }
      if (canAssignJobs && (user.sections || {})["service-requests"]) {
        api.get("/service-requests/assignable-staff").then((r) => setAssignableStaff(r.data)).catch(() => {});
      }
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      else toast.error("Could not load data");
    } finally {
      setLoading(false);
    }
  }, [onLogout, user.sections]);

  useEffect(() => { load(); }, [load]);

  const setters = { enquiries: setEnquiries, applications: setApplications, quotes: setQuotes, "service-requests": setServiceRequests, inquiries: setInquiries };
  const markItem = async (kind, id, status) => {
    try {
      const { data } = await api.patch(`/${kind}/${id}/status`, { status });
      setters[kind]((list) => list.map((x) => (x.id === id ? data : x)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      else toast.error("Could not update status");
    }
  };
  const markStatus = (id, status) => markItem("enquiries", id, status);
  const markAppStatus = (id, status) => markItem("applications", id, status);

  const visibleEnquiries = (filter === "all" ? enquiries : enquiries.filter((e) => (e.status || "new") === filter))
    .filter((e) => deptFilter === "all" || (e.internal && (e.staff?.department || "Unassigned") === deptFilter));
  const visibleApplications = appFilter === "all" ? applications : applications.filter((a) => (a.status || "new") === appFilter);
  const visibleQuotes = quoteFilter === "all" ? quotes : quotes.filter((q) => (q.status || "new") === quoteFilter);
  const visibleSrs = srFilter === "all" ? serviceRequests : serviceRequests.filter((s) => (s.status || "new") === srFilter);
  const visibleInquiries = filter === "all" ? inquiries : inquiries.filter((i) => (i.status || "new") === filter);
  const items = activeTab === "enquiries" ? visibleEnquiries : activeTab === "applications" ? visibleApplications : activeTab === "quotes" ? visibleQuotes : activeTab === "inquiries" ? visibleInquiries : visibleSrs;

  const updateEnquiry = (data) => setEnquiries((list) => list.map((e) => (e.id === data.id ? data : e)));
  const updateApplication = (data) => setApplications((list) => list.map((a) => (a.id === data.id ? data : a)));
  const updateQuote = (data) => setQuotes((list) => list.map((q) => (q.id === data.id ? data : q)));
  const updateSr = (data) => setServiceRequests((list) => list.map((s) => (s.id === data.id ? data : s)));

  const appStatusStyle = (s) =>
    s === "closed"
      ? "text-slate-500 border-slate-600"
      : s === "shortlisted"
        ? "text-emerald-300 border-emerald-500/50"
        : s === "reviewed"
          ? "text-sky-300 border-sky-500/50"
          : "text-nx-goldlight border-nx-gold/40";

  const statusStyle = (s) =>
    s === "closed"
      ? "text-slate-500 border-slate-600"
      : s === "contacted"
        ? "text-sky-300 border-sky-500/50"
        : "text-nx-goldlight border-nx-gold/40";

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-nx-navy2">
        <div className="nx-wrap flex items-center justify-between h-16 gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src={LOGO.iconDark} alt="" className="h-8 w-auto shrink-0" />
            <span className="text-[10px] tracking-[0.2em] sm:tracking-[0.26em] uppercase text-slate-400 truncate">Team Tracker</span>
          </div>
          <div className="flex items-center gap-2.5 sm:gap-4 shrink-0">
            <span className="hidden md:block text-xs text-slate-500 truncate max-w-[180px]">{user.email}</span>
            <a href="/webmail" className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-slate-300 hover:text-nx-goldlight transition-colors" data-testid="admin-mail-link" title="Open NexSecure Mail">
              <Mail size={14} /> <span className="hidden sm:inline">Mail</span>
            </a>
            <button onClick={load} className="text-slate-400 hover:text-nx-goldlight transition-colors p-1.5 sm:p-2" aria-label="Refresh" data-testid="admin-refresh"><RefreshCw size={16} /></button>
            <button onClick={onLogout} className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-slate-300 hover:text-nx-goldlight transition-colors" data-testid="admin-logout"><LogOut size={14} /> <span className="hidden sm:inline">Sign out</span></button>
          </div>
        </div>
      </header>
      <main className="nx-wrap py-6 sm:py-10">
        {activeTab === "home" && <AdminDashboard tabs={allowedTabs} sections={sections} userName={user.name} onOpen={(id, locked) => locked ? toast.error("Your role is not specified for this section. Please contact HR.") : setTab(id)} />}
        {activeTab !== "home" && (
        <div className="flex flex-nowrap sm:flex-wrap gap-2 mb-6 sm:mb-8 overflow-x-auto sm:overflow-visible -mx-4 px-4 sm:mx-0 sm:px-0 pb-2 sm:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="admin-tabs">
          <button onClick={() => setTab("home")} data-testid="admin-tab-home" className="flex items-center gap-2 shrink-0 whitespace-nowrap text-[11px] sm:text-xs tracking-[0.12em] sm:tracking-[0.16em] uppercase px-3.5 sm:px-5 py-2.5 sm:py-3 border border-nx-gold/40 text-nx-goldlight hover:bg-nx-gold/10 transition-colors"><LayoutGrid size={14} /> Dashboard</button>
          {allowedTabs.map((t) => {
            const Icon = t.icon;
            const locked = !sections[t.id];
            return (
              <button key={t.id} onClick={() => locked ? toast.error("Your role is not specified for this section. Please contact HR.") : setTab(t.id)} aria-disabled={locked} data-testid={`admin-tab-${t.id}`} className={`flex items-center gap-2 shrink-0 whitespace-nowrap text-[11px] sm:text-xs tracking-[0.12em] sm:tracking-[0.16em] uppercase px-3.5 sm:px-5 py-2.5 sm:py-3 border transition-colors ${locked ? "border-white/5 text-slate-600 cursor-not-allowed" : activeTab === t.id ? "border-nx-gold/60 text-nx-goldlight bg-nx-gold/[0.06]" : "border-white/10 text-slate-400 hover:text-white"}`}>
                {locked ? <Lock size={13} /> : <Icon size={14} />} {t.label}
              </button>
            );
          })}
        </div>
        )}
        {activeTab === "overview" && <AdminAnalytics onAuthFail={onLogout} />}
        {activeTab === "email-setup" && <AdminEmailSetup onAuthFail={onLogout} user={user} />}
        {activeTab === "holidays" && <AdminHolidays onAuthFail={onLogout} />}
        {activeTab === "leave" && <AdminLeave user={user} onAuthFail={onLogout} />}
        {activeTab === "training" && <AdminTraining user={user} onAuthFail={onLogout} />}
        {activeTab === "career-apps" && <AdminCareerApps user={user} onAuthFail={onLogout} onShortlistToLetters={sections.letters ? (a) => { setLetterPrefill({ employee_name: `${a.first_name || ""} ${a.surname || ""}`.trim(), personal_email: a.email || "", phone: a.mobile_number || "", designation: a.position || "" }); setTab("letters"); } : null} />}
        {activeTab === "people" && <AdminPeople user={user} onAuthFail={onLogout} />}
        {activeTab === "team" && <AdminTeam user={user} onAuthFail={onLogout} readOnly={!canManage("team")} />}
        {activeTab === "letters" && <AdminLetters user={user} onAuthFail={onLogout} prefill={letterPrefill} onPrefillUsed={() => setLetterPrefill(null)} onCreateIdCard={(d) => { setIdCardPrefill(d); setTab("idcards"); }} />}
        {activeTab === "idcards" && <AdminIdCards user={user} onAuthFail={onLogout} prefill={idCardPrefill} onPrefillUsed={() => setIdCardPrefill(null)} />}
        {activeTab === "salary" && <AdminSalary user={user} onAuthFail={onLogout} />}
        {activeTab === "payments" && <AdminPayments onAuthFail={onLogout} readOnly={!canManage("payments")} isAdmin={user.role === "admin"} />}
        {activeTab === "products" && <AdminProducts onAuthFail={onLogout} />}
        {activeTab === "signature" && <AdminSignature user={user} />}
        {activeTab === "enquiries" && (
          <>
            <FilterChips values={["all", "new", "contacted", "closed"]} active={filter} onChange={setFilter} items={enquiries} prefix="admin" />
            {isCeo && staffDepartments.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 -mt-3 mb-6" data-testid="admin-dept-filters">
                <span className="text-[10px] tracking-[0.18em] uppercase text-slate-600 mr-1">Staff department</span>
                {["all", ...staffDepartments].map((d) => (
                  <button key={d} onClick={() => setDeptFilter(d)} data-testid={`admin-dept-filter-${d}`}
                    className={`text-[11px] tracking-[0.14em] uppercase px-3 py-1.5 border transition-colors ${deptFilter === d ? "border-red-500/60 text-red-300 bg-red-500/[0.08]" : "border-white/10 text-slate-500 hover:text-white"}`}>
                    {d === "all" ? "All enquiries" : `${d} (${enquiries.filter((e) => e.internal && (e.staff?.department || "Unassigned") === d).length})`}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        {activeTab === "inquiries" && (
          <FilterChips values={["all", "new", "contacted", "closed"]} active={filter} onChange={setFilter} items={inquiries} prefix="admin-inq" />
        )}
        {activeTab === "quotes" && (
          <>
            <FilterChips values={["all", "new", "contacted", "closed"]} active={quoteFilter} onChange={setQuoteFilter} items={quotes} prefix="admin-quote" />
            {canManage("quotes") && <QuoteCreateForm onCreated={load} />}
          </>
        )}
        {activeTab === "service-requests" && (
          <FilterChips values={["all", "new", "contacted", "closed"]} active={srFilter} onChange={setSrFilter} items={serviceRequests} prefix="admin-sr" />
        )}
        {activeTab === "applications" && (
          <FilterChips values={["all", "new", "reviewed", "shortlisted", "closed"]} active={appFilter} onChange={setAppFilter} items={applications} prefix="admin-app" />
        )}
        {["enquiries", "inquiries", "quotes", "service-requests", "applications"].includes(tab) && (loading ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-slate-500 text-sm" data-testid="admin-empty">Nothing here yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {activeTab === "inquiries"
              ? items.map((i) => (
                  <Row key={i.id}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-medium">{i.name}{i.company ? <span className="text-slate-400 font-normal"> · {i.company}</span> : null}</p>
                        <Meta>{fmtDate(i.created_at)}</Meta>
                      </div>
                      <div className="flex flex-wrap sm:flex-col items-start sm:items-end gap-2 shrink-0">
                        <span className="text-[10px] tracking-[0.16em] uppercase text-nx-goldlight border border-nx-gold/40 px-2.5 py-1 text-right" data-testid={`inquiry-type-label-${i.id}`}>{INQUIRY_LABELS[i.type] || i.type}</span>
                        <span className={`text-[10px] tracking-[0.16em] uppercase border px-2.5 py-1 ${statusStyle(i.status || "new")}`} data-testid={`inquiry-status-${i.id}`}>{i.status || "new"}</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">{i.message}</p>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                      <a href={`tel:${i.phone.replace(/\s/g, "")}`} className="text-xs text-nx-goldlight hover:text-white transition-colors">{i.phone}</a>
                      <a href={`mailto:${i.email}`} className="text-xs text-nx-goldlight hover:text-white transition-colors">{i.email}</a>
                      {canManage("inquiries") && (
                      <span className="flex gap-2 ml-auto">
                        {(i.status || "new") === "new" && (
                          <button onClick={() => markItem("inquiries", i.id, "contacted")} className="text-[11px] tracking-[0.1em] uppercase text-white border border-white/20 hover:border-nx-gold/60 px-3 py-1.5 transition-colors" data-testid={`inquiry-mark-contacted-${i.id}`}>Mark Contacted</button>
                        )}
                        {(i.status || "new") !== "closed" && (
                          <button onClick={() => markItem("inquiries", i.id, "closed")} className="text-[11px] tracking-[0.1em] uppercase text-slate-400 border border-white/20 hover:border-slate-400 px-3 py-1.5 transition-colors" data-testid={`inquiry-mark-closed-${i.id}`}>Close</button>
                        )}
                        {(i.status || "new") !== "new" && (
                          <button onClick={() => markItem("inquiries", i.id, "new")} className="text-[11px] tracking-[0.1em] uppercase text-slate-500 hover:text-white px-3 py-1.5 transition-colors" data-testid={`inquiry-reopen-${i.id}`}>Reopen</button>
                        )}
                      </span>
                      )}
                    </div>
                  </Row>
                ))
              : activeTab === "enquiries"
              ? items.map((e) => (
                  <Row key={e.id} highlight={e.internal && (e.status || "new") !== "closed"} overdue={e.overdue}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-medium flex flex-wrap items-center gap-2">
                          {e.internal && <span className="text-[10px] tracking-[0.16em] uppercase font-semibold bg-red-500/20 text-red-300 border border-red-500/50 px-2 py-0.5" data-testid={`enquiry-internal-${e.id}`}>Internal — Staff</span>}
                          {e.overdue && <span className="text-[10px] tracking-[0.16em] uppercase font-semibold bg-amber-500/20 text-amber-200 border border-amber-500/60 px-2 py-0.5" data-testid={`enquiry-overdue-${e.id}`}>Overdue · 4h+ unanswered</span>}
                          <span>{e.name}{e.organisation ? <span className="text-slate-400 font-normal"> · {e.organisation}</span> : null}</span>
                        </p>
                        <Meta>{fmtDate(e.created_at)} · via {e.source}{e.staff && (e.staff.employee_id || e.staff.department || e.staff.designation) ? ` · ${[e.staff.employee_id, e.staff.department, e.staff.designation].filter(Boolean).join(" · ")}` : ""}</Meta>
                      </div>
                      <div className="flex flex-wrap sm:flex-col items-start sm:items-end gap-2 shrink-0">
                        <span className="text-[10px] tracking-[0.16em] uppercase text-nx-goldlight border border-nx-gold/40 px-2.5 py-1">{e.solution_area}</span>
                        <span className={`text-[10px] tracking-[0.16em] uppercase border px-2.5 py-1 ${statusStyle(e.status || "new")}`} data-testid={`enquiry-status-${e.id}`}>{e.status || "new"}</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">{e.message}</p>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                      <a href={`tel:${e.phone.replace(/\s/g, "")}`} className="text-xs text-nx-goldlight hover:text-white transition-colors">{e.phone}</a>
                      <a href={`mailto:${e.email}`} className="text-xs text-nx-goldlight hover:text-white transition-colors">{e.email}</a>
                      <span className="flex gap-2 ml-auto">
                        {(e.status || "new") === "new" && (
                          <button onClick={() => markStatus(e.id, "contacted")} className="text-[11px] tracking-[0.1em] uppercase text-white border border-white/20 hover:border-nx-gold/60 px-3 py-1.5 transition-colors" data-testid={`enquiry-mark-contacted-${e.id}`}>Mark Contacted</button>
                        )}
                        {(e.status || "new") !== "closed" && (
                          <button onClick={() => markStatus(e.id, "closed")} className="text-[11px] tracking-[0.1em] uppercase text-slate-400 border border-white/20 hover:border-slate-400 px-3 py-1.5 transition-colors" data-testid={`enquiry-mark-closed-${e.id}`}>Close</button>
                        )}
                        {(e.status || "new") !== "new" && (
                          <button onClick={() => markStatus(e.id, "new")} className="text-[11px] tracking-[0.1em] uppercase text-slate-500 hover:text-white px-3 py-1.5 transition-colors" data-testid={`enquiry-reopen-${e.id}`}>Reopen</button>
                        )}
                      </span>
                    </div>
                    <Notes kind="enquiries" item={e} onUpdate={updateEnquiry} readOnly={!canManage("enquiries")} />
                    {isCeo && e.internal && <StaffReply item={e} onUpdate={updateEnquiry} />}
                  </Row>
                ))
              : activeTab === "quotes" ? items.map((q) => (
                  <Row key={q.id}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-medium">{q.name}{q.company ? <span className="text-slate-400 font-normal"> · {q.company}</span> : null}</p>
                        <Meta>{fmtDate(q.created_at)} · {q.location}</Meta>
                      </div>
                      <div className="flex flex-wrap sm:flex-col items-start sm:items-end gap-2 shrink-0">
                        <span className="text-[10px] tracking-[0.16em] uppercase text-nx-goldlight border border-nx-gold/40 px-2.5 py-1">{q.solution}</span>
                        <span className={`text-[10px] tracking-[0.16em] uppercase border px-2.5 py-1 ${statusStyle(q.status || "new")}`} data-testid={`quote-status-${q.id}`}>{q.status || "new"}</span>
                        {q.owner && (
                          <span className={`text-[10px] tracking-[0.16em] uppercase border px-2.5 py-1 ${q.owner === user.email ? "text-emerald-300 border-emerald-500/50" : "text-slate-500 border-white/15"}`} data-testid={`quote-owner-${q.id}`}>
                            {q.owner === user.email ? "My quote" : `By ${q.owner}`}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">{q.requirement}</p>
                    {(q.project_size || q.timeline || q.budget || q.existing_system) && (
                      <p className="text-xs text-slate-500 leading-relaxed">
                        {[
                          q.project_size && `Size: ${q.project_size}`,
                          q.timeline && `Timeline: ${q.timeline}`,
                          q.budget && `Budget: ${q.budget}`,
                          q.existing_system && `Existing system: ${q.existing_system}`,
                        ].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    {q.message && <p className="text-sm text-slate-400 leading-relaxed">{q.message}</p>}
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                      <a href={`tel:${q.phone.replace(/\s/g, "")}`} className="text-xs text-nx-goldlight hover:text-white transition-colors">{q.phone}</a>
                      <a href={`mailto:${q.email}`} className="text-xs text-nx-goldlight hover:text-white transition-colors">{q.email}</a>
                      {q.doc_path && (
                        <button onClick={() => downloadFile(q.doc_path, q.doc_filename)} className="flex items-center gap-1.5 text-xs text-white border border-white/20 hover:border-nx-gold/60 px-3 py-1.5 transition-colors" data-testid={`quote-doc-${q.id}`}>
                          <Download size={12} /> {q.doc_filename || "Document"}
                        </button>
                      )}
                      {canManage("quotes") && <StatusFlow kind="quotes" item={q} onChange={markItem} />}
                    </div>
                    <Notes kind="quotes" item={q} onUpdate={updateQuote} readOnly={!canManage("quotes")} />
                  </Row>
                ))
              : activeTab === "service-requests" ? items.map((s) => (
                  <Row key={s.id}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-medium">{s.name}{s.company ? <span className="text-slate-400 font-normal"> · {s.company}</span> : null}</p>
                        <Meta>{fmtDate(s.created_at)} · {s.site_location}</Meta>
                      </div>
                      <div className="flex flex-wrap sm:flex-col items-start sm:items-end gap-2 shrink-0">
                        <span className="text-[10px] tracking-[0.16em] uppercase text-nx-goldlight border border-nx-gold/40 px-2.5 py-1">{s.service_category}</span>
                        <span className={`text-[10px] tracking-[0.16em] uppercase border px-2.5 py-1 ${s.priority.startsWith("Critical") ? "text-red-400 border-red-500/50" : s.priority.startsWith("High") ? "text-amber-300 border-amber-500/50" : "text-slate-400 border-white/15"}`} data-testid={`sr-priority-${s.id}`}>{s.priority.split(" — ")[0]}</span>
                        <span className={`text-[10px] tracking-[0.16em] uppercase border px-2.5 py-1 ${statusStyle(s.status || "new")}`} data-testid={`sr-status-${s.id}`}>{s.status || "new"}</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">{s.description}</p>
                    {s.system && <p className="text-xs text-slate-500">System: {s.system}{s.preferred_date ? ` · Preferred visit: ${s.preferred_date}` : ""}</p>}
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                      <a href={`tel:${s.phone.replace(/\s/g, "")}`} className="text-xs text-nx-goldlight hover:text-white transition-colors">{s.phone}</a>
                      <a href={`mailto:${s.email}`} className="text-xs text-nx-goldlight hover:text-white transition-colors">{s.email}</a>
                      {s.attachment_path && (
                        <button onClick={() => downloadFile(s.attachment_path, s.attachment_filename)} className="flex items-center gap-1.5 text-xs text-white border border-white/20 hover:border-nx-gold/60 px-3 py-1.5 transition-colors" data-testid={`sr-attachment-${s.id}`}>
                          <Download size={12} /> {s.attachment_filename || "Attachment"}
                        </button>
                      )}
                      {canManage("service-requests") && <StatusFlow kind="service-requests" item={s} onChange={markItem} />}
                      {canAssignJobs && <AssignControl item={s} staff={assignableStaff} onAssigned={load} />}
                      {!canAssignJobs && s.assigned_to && (
                        <span className="text-[10px] tracking-[0.14em] uppercase text-slate-500 border border-white/10 px-2.5 py-1" data-testid={`sr-assigned-${s.id}`}>Assigned: {s.assigned_to}</span>
                      )}
                    </div>
                    <Notes kind="service-requests" item={s} onUpdate={updateSr} readOnly={!canManage("service-requests")} />
                  </Row>
                ))
              : items.map((a) => (
                  <Row key={a.id}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-medium break-words">{a.name}</p>
                        <Meta>{fmtDate(a.created_at)}</Meta>
                      </div>
                      <div className="flex flex-wrap sm:flex-col items-start sm:items-end gap-2 shrink-0 sm:max-w-[45%]">
                        <span className="text-[10px] tracking-[0.16em] uppercase text-nx-goldlight border border-nx-gold/40 px-2.5 py-1 text-right break-words">{a.role}</span>
                        <span className={`text-[10px] tracking-[0.16em] uppercase border px-2.5 py-1 ${appStatusStyle(a.status || "new")}`} data-testid={`application-status-${a.id}`}>{a.status || "new"}</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed">{a.message}</p>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                      <a href={`tel:${a.phone.replace(/\s/g, "")}`} className="text-xs text-nx-goldlight hover:text-white transition-colors">{a.phone}</a>
                      <a href={`mailto:${a.email}`} className="text-xs text-nx-goldlight hover:text-white transition-colors">{a.email}</a>
                      {a.cv_path && (
                        <button onClick={() => downloadCv(a.cv_path, a.cv_filename)} className="flex items-center gap-1.5 text-xs text-white border border-white/20 hover:border-nx-gold/60 px-3 py-1.5 transition-colors" data-testid={`admin-cv-${a.id}`}>
                          <Download size={12} /> {a.cv_filename || "CV"}
                        </button>
                      )}
                      {a.photo_path && (
                        <button onClick={() => downloadFile(a.photo_path, a.photo_filename)} className="flex items-center gap-1.5 text-xs text-white border border-white/20 hover:border-nx-gold/60 px-3 py-1.5 transition-colors" data-testid={`admin-photo-${a.id}`}>
                          <Download size={12} /> {a.photo_filename || "Photo"}
                        </button>
                      )}
                      {canManage("applications") && (
                      <span className="flex flex-wrap gap-2 ml-auto">
                        {(a.status || "new") === "new" && (
                          <button onClick={() => markAppStatus(a.id, "reviewed")} className="text-[11px] tracking-[0.1em] uppercase text-white border border-white/20 hover:border-nx-gold/60 px-3 py-1.5 transition-colors" data-testid={`application-mark-reviewed-${a.id}`}>Mark Reviewed</button>
                        )}
                        {((a.status || "new") === "new" || a.status === "reviewed") && (
                          <button onClick={() => markAppStatus(a.id, "shortlisted")} className="text-[11px] tracking-[0.1em] uppercase text-emerald-300 border border-emerald-500/40 hover:border-emerald-400 px-3 py-1.5 transition-colors" data-testid={`application-shortlist-${a.id}`}>Shortlist</button>
                        )}
                        {(a.status || "new") !== "closed" && sections.letters && (
                          <button onClick={() => shortlistToLetters(a)} className="flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-nx-goldlight border border-nx-gold/50 hover:bg-nx-gold/10 px-3 py-1.5 transition-colors" data-testid={`application-shortlist-docs-${a.id}`} title="Shortlist and open the appointment form pre-filled from this application">
                            <FileSignature size={12} /> Shortlist &amp; Send Documents
                          </button>
                        )}
                        {(a.status || "new") !== "closed" && (
                          <button onClick={() => markAppStatus(a.id, "closed")} className="text-[11px] tracking-[0.1em] uppercase text-slate-400 border border-white/20 hover:border-slate-400 px-3 py-1.5 transition-colors" data-testid={`application-mark-closed-${a.id}`}>Close</button>
                        )}
                        {(a.status || "new") !== "new" && (
                          <button onClick={() => markAppStatus(a.id, "new")} className="text-[11px] tracking-[0.1em] uppercase text-slate-500 hover:text-white px-3 py-1.5 transition-colors" data-testid={`application-reopen-${a.id}`}>Reopen</button>
                        )}
                      </span>
                      )}
                    </div>
                    <Notes kind="applications" item={a} onUpdate={updateApplication} readOnly={!canManage("applications")} />
                  </Row>
                ))}
          </div>
        ))}
        <p className="mt-10 text-xs text-slate-600 flex items-center gap-2"><ExternalLink size={12} /> These lists are private — only signed-in team members can see them.</p>
      </main>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [initialTab, setInitialTab] = useState("home");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam) setInitialTab(tabParam);
    const ssoError = params.get("sso_error");
    if (ssoError) toast.error(`People portal sign-in failed: ${ssoError}`);
    if (tabParam || ssoError) window.history.replaceState({}, "", "/admin");
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
    const kick = () => setUser(false);
    window.addEventListener("nx-force-logout", kick);
    return () => window.removeEventListener("nx-force-logout", kick);
  }, []);
  if (user === null) return <div className="min-h-screen bg-nx-navy" />;
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* session cleared regardless */ }
    setUser(false);
  };
  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased min-h-screen" data-testid="admin-page">
      {user ? <Dashboard user={user} onLogout={logout} initialTab={initialTab} /> : <Login onSuccess={setUser} />}
    </div>
  );
}
